'use client'
import { useCallback } from 'react'
import type { Option, PaymentProviderConfig } from 'ecosystem'
import { getVatRateByCountryCode, shippingForBuyer } from 'shared-utils'
import { getCountryCodeFromPaymentConfig } from 'api/utils/payment/getCountryCodeFromPaymentConfig'
import { useBuyerVat } from './useBuyerVat'

/**
 * Convert a carrier's shipping quote onto the logged-in buyer's VAT treatment.
 *
 * @remarks
 * Ingrid, PostNord and Nshift all quote a price that is VAT-inclusive at the
 * *store's* rate — the carrier has no idea who the buyer is. A zero-rated buyer
 * owes no VAT on freight any more than on the goods (the merchant's own
 * intra-community invoices show 0 % on the freight line too), so the quote has
 * to come down to its net value rather than simply being relabelled 0 %.
 *
 * Applied here, at the point the quote is read, so the draft, the review panel
 * and the submitted order all show the same number. The submit handler
 * deliberately does *not* convert again.
 *
 * Returns the quote unchanged for domestic buyers.
 */
export const useBuyerShipping = (paymentProviderConfig?: Option<PaymentProviderConfig>) => {
  const buyerVat = useBuyerVat(paymentProviderConfig)
  const domesticRate = getVatRateByCountryCode(
    getCountryCodeFromPaymentConfig(paymentProviderConfig)
  )

  const rebaseShippingQuote = useCallback(
    (grossQuote: number) => shippingForBuyer(grossQuote, domesticRate, buyerVat).cost,
    [buyerVat, domesticRate]
  )

  return { buyerVat, domesticRate, rebaseShippingQuote }
}
